import { lazy, Suspense, useEffect, useState, Component, type ErrorInfo, type ReactNode } from "react"
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { AnimatePresence } from "framer-motion"

import { useAuthStore, useBrandStore } from "@/stores"
import { permisoDenegado, rutaInicio } from "@/lib/permisos"
import { MainLayout, AuthLayout } from "@/components/layout"
import { SplashScreen, Toaster, PageTransition } from "@/components/ui"
import { ForcedPasswordChange } from "@/pages/auth/ForcedPasswordChange"

// Lazy load pages — cada ruta es un chunk separado, recarga sólo lo necesario.
const LoginPage = lazy(() => import("@/pages/auth").then((m) => ({ default: m.LoginPage })))
const ForgotPasswordPage = lazy(() => import("@/pages/auth").then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import("@/pages/auth").then((m) => ({ default: m.ResetPasswordPage })))
const DashboardPage = lazy(() => import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage })))
const UsersPage = lazy(() => import("@/pages/users").then((m) => ({ default: m.UsersPage })))
const UserFormPage = lazy(() => import("@/pages/users").then((m) => ({ default: m.UserFormPage })))
const UserDetailPage = lazy(() => import("@/pages/users").then((m) => ({ default: m.UserDetailPage })))
const WalletsPage = lazy(() => import("@/pages/wallets").then((m) => ({ default: m.WalletsPage })))
const SimsPage = lazy(() => import("@/pages/sims").then((m) => ({ default: m.SimsPage })))
const DepositsPage = lazy(() => import("@/pages/deposits").then((m) => ({ default: m.DepositsPage })))
const SalesPage = lazy(() => import("@/pages/sales").then((m) => ({ default: m.SalesPage })))
const RechargePage = lazy(() => import("@/pages/sales").then((m) => ({ default: m.RechargePage })))
const PortabilityActivationPage = lazy(() => import("@/pages/sales").then((m) => ({ default: m.PortabilityActivationPage })))
const VincularTitularPage = lazy(() => import("@/pages/sales").then((m) => ({ default: m.VincularTitularPage })))
const ConsultaPortabilidadPage = lazy(() => import("@/pages/sales").then((m) => ({ default: m.ConsultaPortabilidadPage })))
const ContentPage = lazy(() => import("@/pages/content").then((m) => ({ default: m.ContentPage })))
const ProfilePage = lazy(() => import("@/pages/profile").then((m) => ({ default: m.ProfilePage })))
const SettingsPage = lazy(() => import("@/pages/settings").then((m) => ({ default: m.SettingsPage })))
const PermissionProfilesPage = lazy(() => import("@/pages/settings").then((m) => ({ default: m.PermissionProfilesPage })))
const BrandSmtpPage = lazy(() => import("@/pages/settings").then((m) => ({ default: m.BrandSmtpPage })))
const ReportsPage = lazy(() => import("@/pages/reports").then((m) => ({ default: m.ReportsPage })))
const CommissionsPage = lazy(() => import("@/pages/commissions").then((m) => ({ default: m.CommissionsPage })))
const LineProfilePage = lazy(() => import("@/pages/line-profile/LineProfilePage"))
const RecargasExternasPage = lazy(() => import("@/pages/recargas-externas").then((m) => ({ default: m.RecargasExternasPage })))
import { puedeRecargasTerceros } from "@/pages/recargas-externas/access"

// Fallback discreto mientras carga un chunk.
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
    </div>
  )
}

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // Password temporal autogenerado: bloquear TODO el acceso hasta que el
  // usuario fije su propia contraseña.
  if (user?.debe_cambiar_password) {
    return <ForcedPasswordChange />
  }

  return <>{children}</>
}

// Gate del módulo Recargas de terceros: allowlist de email (canary) o, si no
// hay allowlist, gating normal por tipo (B/C/R). Defensa en profundidad sobre
// el menú; el backend también valida.
function RecargasTercerosGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!puedeRecargasTerceros(user)) {
    return <Navigate to={rutaInicio(user)} replace />
  }
  return <>{children}</>
}

// Gating duro por tipo de usuario. Si el usuario fuerza una URL que su
// tipo no permite, redirige a su ruta de aterrizaje permitida (rutaInicio).
// Defensa en profundidad sobre el gating UI del sidebar; el backend también
// valida. NO redirigir fijo a /index: si el perfil deniega home_all, /index
// también está gateado y un destino fijo produciría un loop de redirects.
function TipoGate({
  allowed,
  permiso,
  children,
}: {
  allowed?: string[]
  /** Clave de permiso (ej. "ventas_all"). Si el perfil del usuario la deniega
   *  ("n"/"h"), la ruta se bloquea aunque el tipo esté permitido. Refleja el
   *  gating del menú para que no se pueda abrir la página por URL directa. */
  permiso?: string
  children: React.ReactNode
}) {
  const { user } = useAuthStore()
  if (!user || (allowed && !allowed.includes(user.tipo_usuario))) {
    return <Navigate to={rutaInicio(user)} replace />
  }
  if (permiso && permisoDenegado(user.permisos as Record<string, string> | null | undefined, permiso)) {
    return <Navigate to={rutaInicio(user)} replace />
  }
  return <>{children}</>
}

// Perfil con todos los módulos denegados: no hay a dónde aterrizar.
function SinAccesoPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
      <h2 className="text-lg font-semibold">Sin módulos habilitados</h2>
      <p className="text-sm text-muted-foreground max-w-md">
        Tu perfil de permisos no tiene ningún módulo habilitado. Pide al titular
        de tu cuenta que te asigne un perfil con los permisos que necesitas.
      </p>
    </div>
  )
}

// Public Route Component (redirects if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()

  if (isAuthenticated) {
    return <Navigate to={rutaInicio(user)} replace />
  }

  return <>{children}</>
}

// Error boundary para capturar fallos en páginas (evita pantalla blanca)
class PageErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("PageErrorBoundary:", error, info.componentStack)
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-6 text-center">
          <h2 className="text-lg font-semibold text-destructive">Error al cargar la página</h2>
          <p className="text-sm text-muted-foreground max-w-md">{this.state.error.message}</p>
          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Reintentar
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// Animated Routes wrapper
function AnimatedRoutes() {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
      <Routes location={location} key={location.pathname}>
        {/* Public Routes - Login */}
        <Route
          element={
            <PublicRoute>
              <AuthLayout />
            </PublicRoute>
          }
        >
          <Route path="/" element={
            <PageTransition>
              <LoginPage />
            </PageTransition>
          } />
          <Route path="/forgot-password" element={
            <PageTransition>
              <ForgotPasswordPage />
            </PageTransition>
          } />
          <Route path="/reset-password" element={
            <PageTransition>
              <ResetPasswordPage />
            </PageTransition>
          } />
          <Route path="/register" element={
            <PageTransition>
              <LoginPage />
            </PageTransition>
          } />
        </Route>

        {/* Protected Routes - Rutas exactas del sistema original */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* /index - Inicio (Dashboard). Gateado con home_all: un empleado
              cuyo perfil deniega el Dashboard ya no aterriza aquí (rebota a
              su primera ruta permitida vía rutaInicio). */}
          <Route path="/index" element={
            <TipoGate permiso="home_all">
              <PageTransition>
                <DashboardPage />
              </PageTransition>
            </TipoGate>
          } />
          <Route path="/dashboard" element={<Navigate to="/index" replace />} />

          {/* /sin-acceso - perfil con todos los módulos denegados */}
          <Route path="/sin-acceso" element={
            <PageTransition>
              <SinAccesoPage />
            </PageTransition>
          } />
          
          {/* /ventas - Ventas (Admin/A/B/C/R; M no opera) */}
          <Route path="/ventas" element={
            <TipoGate allowed={["Admin","A","B","C","R"]} permiso="ventas_all">
              <PageErrorBoundary>
                <PageTransition>
                  <SalesPage />
                </PageTransition>
              </PageErrorBoundary>
            </TipoGate>
          } />
          <Route path="/ventas/nueva" element={<Navigate to="/portabilidad-activacion" replace />} />

          {/* /saldos - Saldos (M/B/C/R; Admin y A no tienen wallet) */}
          <Route path="/saldos" element={
            <TipoGate allowed={["A","B","C","R"]} permiso="saldos_all">
              <PageTransition>
                <WalletsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /inventario-de-sims - Inventario de SIMs (Admin/A/B/C) */}
          <Route path="/inventario-de-sims" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="inventarios_all">
              <PageTransition>
                <SimsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /sims-personales - SIMs personales */}
          <Route path="/sims-personales" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="inventarios_all">
              <PageTransition>
                <SimsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /recarga - solo Recarga (B/C/R) */}
          <Route path="/recarga" element={
            <TipoGate allowed={["B","C","R"]} permiso="activacion_all">
              <PageTransition>
                <RechargePage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /recargas-servicios - venta de TAE de terceros + servicios.
              Acceso por allowlist de email (canary) o tipo B/C/R. */}
          <Route path="/recargas-servicios" element={
            <RecargasTercerosGate>
              <PageTransition>
                <RecargasExternasPage />
              </PageTransition>
            </RecargasTercerosGate>
          } />

          {/* /portabilidad-activacion - Portar / Activar / Cambio región (B/C) */}
          <Route path="/portabilidad-activacion" element={
            <TipoGate allowed={["B","C"]} permiso="activacion_all">
              <PageTransition>
                <PortabilityActivationPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /vincular-titular - Vincular/reintentar titular de línea activa (B/C) */}
          <Route path="/vincular-titular" element={
            <TipoGate allowed={["B","C"]} permiso="activacion_all">
              <PageTransition>
                <VincularTitularPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /consulta-portabilidad - Estado de una portabilidad por folio (B/C). Acepta ?pid= */}
          <Route path="/consulta-portabilidad" element={
            <TipoGate allowed={["B","C"]} permiso="activacion_all">
              <PageTransition>
                <ConsultaPortabilidadPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /consulta-de-perfil - Consulta de perfil de línea (solo lectura, B) */}
          <Route path="/consulta-de-perfil" element={
            <TipoGate allowed={["B"]} permiso="consulta_perfil_all">
              <PageErrorBoundary>
                <PageTransition>
                  <LineProfilePage />
                </PageTransition>
              </PageErrorBoundary>
            </TipoGate>
          } />

          {/* Redirigir rutas legacy */}
          <Route path="/portabilidad" element={<Navigate to="/portabilidad-activacion" replace />} />
          <Route path="/activacion" element={<Navigate to="/portabilidad-activacion" replace />} />

          {/* /consulta-imei: ahora vive como tab dentro de Portabilidad y Activación. */}
          <Route path="/consulta-imei" element={<Navigate to="/portabilidad-activacion" replace />} />
          
          {/* /compras - Compras / Depósitos (B/C/R; A opera a crédito, M no opera) */}
          <Route path="/compras" element={
            <TipoGate allowed={["A","B","C","R"]} permiso="compras_all">
              <PageTransition>
                <DepositsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /perfil - Perfil (todos, salvo que el perfil de permisos lo deniegue) */}
          <Route path="/perfil" element={
            <TipoGate permiso="perfil_all">
              <PageTransition>
                <ProfilePage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /admin/smtp-marca - SMTP por marca (Admin/A) */}
          <Route path="/admin/smtp-marca" element={
            <TipoGate allowed={["Admin","A"]} permiso="configuracion_all">
              <PageTransition>
                <BrandSmtpPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /usuarios - Usuarios (Admin/A/B; el C sólo sus empleados) */}
          <Route path="/usuarios" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="usuarios_all">
              <PageTransition>
                <UsersPage />
              </PageTransition>
            </TipoGate>
          } />
          <Route path="/usuarios/nuevo" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="usuarios_all">
              <PageTransition>
                <UserFormPage />
              </PageTransition>
            </TipoGate>
          } />
          <Route path="/usuarios/:id" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="usuarios_all">
              <PageTransition>
                <UserDetailPage />
              </PageTransition>
            </TipoGate>
          } />
          <Route path="/usuarios/:id/editar" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="usuarios_all">
              <PageTransition>
                <UserFormPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /reportes - Reportes (Admin/A/M/B/C; R no ve reportería) */}
          <Route path="/reportes" element={
            <TipoGate allowed={["Admin","A","M","B","C"]} permiso="reportes_all">
              <PageTransition>
                <ReportsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /publicidad-y-manuales - Publicidad y Manuales (todos, salvo denegado) */}
          <Route path="/publicidad-y-manuales" element={
            <TipoGate permiso="publicidad_y_anuncios_all">
              <PageTransition>
                <ContentPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /comisiones - Comisiones (Admin/A/B/M; C y R no reciben comisión variable) */}
          <Route path="/comisiones" element={
            <TipoGate allowed={["Admin","A","M","B"]} permiso="comisiones_all">
              <PageTransition>
                <CommissionsPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /perfiles-permiso - Perfiles de Permisos (Admin/A/B; el C para sus empleados) */}
          <Route path="/perfiles-permiso" element={
            <TipoGate allowed={["Admin","A","B","C"]} permiso="usuarios_all">
              <PageTransition>
                <PermissionProfilesPage />
              </PageTransition>
            </TipoGate>
          } />

          {/* /configuracion - Configuración (Admin only) */}
          <Route path="/configuracion" element={
            <TipoGate allowed={["Admin"]} permiso="configuracion_all">
              <PageTransition>
                <SettingsPage />
              </PageTransition>
            </TipoGate>
          } />
        </Route>

        {/* Redirects */}
        <Route path="*" element={<Navigate to="/index" replace />} />
      </Routes>
      </Suspense>
    </AnimatePresence>
  )
}

function AppContent() {
  const { initializeBrand } = useBrandStore()
  const { refreshUser, isAuthenticated } = useAuthStore()
  const [showSplash, setShowSplash] = useState(true)

  useEffect(() => {
    // Initialize brand on app start
    initializeBrand()
  }, [initializeBrand])

  useEffect(() => {
    // Refresh user data if authenticated
    if (isAuthenticated) {
      refreshUser()
    }
  }, [isAuthenticated, refreshUser])

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} minDuration={2000} />
  }

  return (
    <>
      <AnimatedRoutes />
      <Toaster />
    </>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
